import { DataConnection } from 'peerjs';
import { create, StateCreator, StoreApi, StoreMutatorIdentifier, UseBoundStore } from 'zustand';

import { PeerConnection } from './peer.js';

export interface RealTimeSharingOptions<S, SS = S> {
  reviver?: (key: string, value: any) => any;
  replacer?: (key: string, value: any) => any;
  partialize?: (state: S) => SS;
}

interface RealTimeSharingApi {
  status: 'listening' | 'connected' | 'disconnected';
  mode: 'host' | 'guest';
  connections: DataConnection[];
  connect: (sid: string) => Promise<void>;
  serve: (sid: string) => Promise<void>;
  revoke: (sid: string) => void;
  stop: () => void;
}

const createStoreRts = <S, SS = S>(store: StoreApi<S>, opts?: RealTimeSharingOptions<S, SS>) =>
  create<RealTimeSharingApi>((set) => {
    const peer = new PeerConnection<{
      type: 'sync';
      data: ArrayBuffer;
      source: string;
    }>();
    const partialize = opts?.partialize || ((s: S) => s as unknown as SS);
    const replacer = opts?.replacer || ((key: string, value: any) => value);
    const reviver = opts?.reviver || ((key: string, value: any) => value);

    let status: 'listening' | 'connected' | 'disconnected' = 'disconnected';
    let mode: 'host' | 'guest' = 'host';
    const connections: DataConnection[] = [];

    let unsubscribe: () => void = () => {};

    const connect = async (sid: string) => {
      set({ mode: 'guest' });

      const oldState = JSON.stringify(partialize(store.getState()), replacer);
      const decoder = new TextDecoder();
      const restore = () => {
        set({ status: 'disconnected' });
        unsubscribe();
        store.setState(JSON.parse(oldState, reviver));
      };

      unsubscribe();
      unsubscribe = peer.subscribe((data) => {
        switch (data.type) {
          case 'sync':
            store.setState((prevState) => ({
              ...prevState,
              ...JSON.parse(decoder.decode(data.data), reviver),
            }));
            break;
        }
      });

      peer.once('connected', () => {
        set({ status: 'connected' });
      });

      peer.once('close', restore);

      try {
        await peer.connect(sid);
      } catch (err) {
        restore();
        throw err;
      }
    };

    /**
     * Start hosting listening
     * @param id
     */
    const serve = async (id: string) => {
      set({ mode: 'host' });

      const sync = (target?: string) => {
        if (target) {
          peer.send(target, {
            type: 'sync',
            data: Buffer.from(JSON.stringify(partialize(store.getState()), replacer)),
            source: id,
          });
        } else {
          peer.broadcast({
            type: 'sync',
            data: Buffer.from(JSON.stringify(partialize(store.getState()), replacer)),
            source: id,
          });
        }
      };

      unsubscribe();
      unsubscribe = store.subscribe(() => {
        sync();
      });

      const handleConnection = (connection: DataConnection) => {
        connection.once('open', () => {
          sync(connection.peer);
          set({ connections: Array.from(peer.connections.values()) });
        });

        connection.once('close', () => {
          console.log('connection closed');
          set({ connections: Array.from(peer.connections.values()) });
        });
      };

      peer.once('open', () => {
        set({ status: 'listening' });
      });

      peer.on('connection', handleConnection);

      peer.once('close', () => {
        set({ status: 'disconnected' });
        peer.off('connection', handleConnection);
        unsubscribe();
      });

      await peer.serve(id);
    };

    const stop = () => {
      unsubscribe();
      peer.close();
    };

    const revoke = (sid: string) => {
      peer.revoke(sid);
    };

    return {
      status,
      mode,
      connections,
      connect,
      serve,
      stop,
      revoke,
    };
  });

export type StoreRealTimeSharing<S, SS> = {
  rts: UseBoundStore<StoreApi<RealTimeSharingApi>>;
};

type RealTimeSharingImpl = <S, SS = S>(
  storeInitializer: StateCreator<S, [], []>,
  options?: RealTimeSharingOptions<S, SS>
) => StateCreator<S, [], []>;

const rtsImpl: RealTimeSharingImpl = (config, opts) => (set, get, api) =>
  config(
    set,
    get,
    Object.assign(api, {
      rts: createStoreRts(api, opts as any),
    })
  );

type Write<T extends object, U extends object> = Omit<T, keyof U> & U;

type WithRealTimeSharing<S, A> = S extends { getState: () => infer T }
  ? Write<S, StoreRealTimeSharing<T, A>>
  : never;

type RealTimeSharing = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
  U = T
>(
  initializer: StateCreator<T, [...Mps, ['spz/rts', unknown]], Mcs>,
  options?: RealTimeSharingOptions<T, U>
) => StateCreator<T, Mps, [['spz/rts', U], ...Mcs]>;

declare module 'zustand' {
  // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars, @typescript-eslint/no-unused-vars
  interface StoreMutators<S, A> {
    'spz/rts': WithRealTimeSharing<S, A>;
  }
}

export const rts = rtsImpl as unknown as RealTimeSharing;
